import { cn } from '@tempmee/core';
import { useState } from 'react';
import { Box, Button, CircularProgress } from '@tempmee/react';
import {
  CandidateDetails,
  type CandidateDetailsInterviewTab,
  CandidateTopper,
} from '@tempmee/react-blocks';
import {
  COLLAPSE_BREAKPOINT,
  DetailsPageShell,
  DetailsPanel,
} from '~/components/shared/DetailsPage';
import {
  type CandidateStatus,
  type IGetCandidateOnsiteInterviewListResult,
  useGetCandidateResume,
} from '~/domain/PermanentJobs';
import { useMediaQuery } from '~/hooks';
import type { IUser } from '~/models';
import { toProfessionalTopperData } from '~/utils/professionalTopper';
import { resolveProfession } from '~/utils/shiftCard';
import { CANDIDATE_DETAILS_COPY } from './candidateDetailsView';
import { OnsiteInterviewsList } from './containers';
import { useCompanyMatchLabel } from './hooks/useCompanyMatchLabel';
import { toInterviewCandidateDetailsProps } from './utils/toInterviewCandidateDetailsProps';

interface ICandidateDetailsPageProps {
  professional: IUser;
  postingId?: string;
  officeId: string;
  /** The posting's profession; an interview carries it too, as a fallback. */
  postingProfession?: string;
  interviews?: IGetCandidateOnsiteInterviewListResult;
  /**
   * The candidate's resolved status. The interview list carries no status when
   * the candidate has no interviews, so the caller falls back to the candidate.
   */
  officeStatus?: CandidateStatus;
  hasWorkedWithDSO: boolean;
  hasWorkedWithOffice: boolean;
  canHire: boolean;
  canReject: boolean;
  onHireClick: () => void;
  onRejectClick: () => void;
}

/**
 * The redesigned Candidate Details page (GOTU-8148), built from the Figma
 * `Type=Interview` frames: the DS candidate topper with Reject and Hire over
 * the DS `CandidateDetails` Interview block (GoTu Resume / Experience & Skills),
 * beside the candidate's interviews. Web keeps Reject and Hire in the topper at
 * every width; the responsive frame draws no footer. `CandidateOverview` owns
 * the data and the hire/reject dialogs.
 */
export const CandidateDetailsPage = ({
  professional,
  postingId,
  officeId,
  postingProfession,
  interviews,
  officeStatus,
  hasWorkedWithDSO,
  hasWorkedWithOffice,
  canHire,
  canReject,
  onHireClick,
  onRejectClick,
}: ICandidateDetailsPageProps) => {
  // Today's page draws this chip through `ProfessionalProfile`, which resolves it with no job.
  const companyMatch = useCompanyMatchLabel({
    offerOwnerId: '',
    professional,
    hasWorkedWithDSO,
    hasWorkedWithOffice,
  });

  // The block has no container query, so the summary row's divider direction
  // follows the shell's collapse, as on the Shift Details pages.
  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);

  // Held here, not in the block: the shell remounts its primary column when it
  // collapses, which would put an uncontrolled switcher back on the resume.
  const [tab, setTab] = useState<CandidateDetailsInterviewTab>('gotu-resume');

  const { data: resume, isInitialLoading: isResumeLoading } = useGetCandidateResume(
    { professionalId: professional.id, postingId },
    { enabled: !!postingId && !!professional.id },
  );

  const topperData = toProfessionalTopperData(professional);

  // The posting's profession picks the role code and the specialties shown;
  // every interview belongs to the posting, so the first one is the fallback.
  const profession =
    postingProfession ?? resolveProfession(interviews?.maps[interviews.data[0]]?.profession);

  return (
    <DetailsPageShell
      testIDPrefix="candidate-details"
      title={CANDIDATE_DETAILS_COPY.title}
      primary={
        // Figma 7113:49239/49240: topper inset 16 (8 collapsed), 16 down to the
        // switcher, and the block's own 40 below the panel.
        <DetailsPanel
          className={cn('p-0', isCollapsed ? 'pb-2' : 'gap-2 pb-0')}
          data-testid="candidate-details-panel"
          data-cy="candidate-details-panel"
        >
          <Box pt={isCollapsed ? 2 : 4} px={isCollapsed ? 2 : 4}>
            <CandidateTopper
              {...topperData}
              // The Figma centres the name, role and signals on the avatar.
              identityAlign="center"
              roleCode={profession}
              companyExperience={companyMatch?.label}
              testID="candidate-details-topper"
              actions={[
                <Button
                  key="reject"
                  variant="outlined"
                  color="secondary"
                  disabled={!canReject}
                  onClick={onRejectClick}
                  data-cy="candidate-action-reject"
                >
                  Reject
                </Button>,
                <Button
                  key="hire"
                  disabled={!canHire}
                  onClick={onHireClick}
                  data-cy="candidate-action-hire"
                >
                  Hire
                </Button>,
              ]}
            />
          </Box>
          {/*
            Wait for the resume: the block drops its switcher without one, so
            rendering early would flash Experience & Skills before the tabs.
          */}
          {isResumeLoading ? (
            <Box
              className="flex justify-center py-6"
              data-testid="candidate-details-resume-loading"
            >
              <CircularProgress />
            </Box>
          ) : (
            <CandidateDetails
              layout={isCollapsed ? 'mobile' : 'desktop'}
              tab={tab}
              onTabChange={setTab}
              {...toInterviewCandidateDetailsProps({ professional, profession, resume })}
              data-testid="candidate-details-profile"
            />
          )}
        </DetailsPanel>
      }
      sidebar={
        interviews && (
          <OnsiteInterviewsList
            postingId={postingId ?? ''}
            maps={interviews.maps}
            officeId={officeId}
            officeStatus={officeStatus ?? interviews.officeStatus}
            professionalId={professional.id}
            professionalName={professional.full_name || ''}
          />
        )
      }
    />
  );
};
